import type { TCEmployeeExecution } from 'src/api/generated';
import {
  PiCheck,
  PiClock,
  PiCopy,
  PiSpinnerGap,
  PiUser,
  PiWarning,
  PiXCircle,
} from 'react-icons/pi';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardBody } from 'src/modules/shared/components/atoms/cards/card-body';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useCopyToClipboard } from 'src/modules/shared/hooks/copy-to-clipboard';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { ComponentVariant } from 'src/modules/shared/types/component.type';
import { formatDate } from 'src/modules/shared/utils/formatting/date.util';
import { formatDurationFromDates } from 'src/modules/shared/utils/formatting/time.util';
import { getExecutionDisplayTitle } from 'src/modules/automations/utils/executions/display.util';
import {
  ExecutionOutcome,
  type ExecutionOutcomeValue,
} from 'src/modules/automations/types/executions/execution-presentation.type';
import {
  getExecutionBlocker,
  getExecutionContactName,
  getExecutionOutcome,
} from 'src/modules/automations/utils/executions/execution-presentation.util';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { getEmployeeBlockerPresentation } from 'src/modules/automations/utils/employees/employee-blocker-presentation.util';

interface EmployeeExecutionCardProps {
  /** Execution represented by the card. */
  execution: TCEmployeeExecution;
  /** Opens the execution detail. */
  onSelect: () => void;
}

interface OutcomePresentation {
  /** Translation key for the status label. */
  labelKey: TranslationKey;
  /** Shared semantic component variant. */
  variant: ComponentVariant;
  /** Status icon. */
  icon: React.ReactNode;
}

const OUTCOME_PRESENTATION: Record<ExecutionOutcomeValue, OutcomePresentation> =
  {
    [ExecutionOutcome.COMPLETED]: {
      labelKey: 'automations.aiAgents.executions.status.completed',
      variant: 'success',
      icon: <PiCheck className="h-3.5 w-3.5" />,
    },
    [ExecutionOutcome.FAILED]: {
      labelKey: 'automations.aiAgents.executions.status.failed',
      variant: 'danger',
      icon: <PiXCircle className="h-3.5 w-3.5" />,
    },
    [ExecutionOutcome.RUNNING]: {
      labelKey: 'automations.aiAgents.executions.status.running',
      variant: 'info',
      icon: <PiSpinnerGap className="h-3.5 w-3.5 animate-spin" />,
    },
    [ExecutionOutcome.BLOCKED]: {
      labelKey: 'automations.aiAgents.executions.status.blocked',
      variant: 'warning',
      icon: <PiWarning className="h-3.5 w-3.5" />,
    },
    [ExecutionOutcome.UNKNOWN]: {
      labelKey: 'automations.aiAgents.executions.status.unknown',
      variant: 'neutral',
      icon: <AiRobotOutlineIcon className="h-3.5 w-3.5" />,
    },
  };

/** Unified responsive execution card for every business outcome. */
const EmployeeExecutionCard: React.FC<EmployeeExecutionCardProps> = ({
  execution,
  onSelect,
}) => {
  const { t } = useI18n();
  const { copyToClipboard, isCopied } = useCopyToClipboard();
  const outcome = getExecutionOutcome(execution);
  const presentation = OUTCOME_PRESENTATION[outcome];
  const title =
    getExecutionDisplayTitle(execution, t) ||
    t('automations.aiAgents.executions.genericTitle');
  const contactName = getExecutionContactName(execution);
  const duration = formatDurationFromDates(
    execution.createdAt,
    execution.updatedAt
  );
  const blocker = getExecutionBlocker(execution);
  const structuredBlocker = blocker?.structured;
  const blockerPresentation = structuredBlocker
    ? getEmployeeBlockerPresentation(structuredBlocker, t)
    : undefined;
  const blockerCategory =
    blockerPresentation?.categoryLabel ??
    t('automations.aiAgents.executions.blocker');
  const blockerContext = blockerPresentation?.context;
  const blockerReason =
    blockerPresentation?.reason ??
    blocker?.reason ??
    t('automations.aiAgents.executions.blockerReasonFallback');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <Card
      variant={outcome === ExecutionOutcome.BLOCKED ? 'warning' : 'secondary'}
      appearance="outline"
      shadow="none"
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="border-neutral-200 bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <CardBody className="p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Text
                as="h3"
                size="small"
                weight="semibold"
                className="min-w-0 text-primary-950"
              >
                {title}
              </Text>
              <StatusPill
                label={t(presentation.labelKey)}
                variant={presentation.variant}
                appearance="soft"
                size="xs"
                icon={presentation.icon}
                className="px-2 py-0.5"
              />
            </div>

            <div className="mt-2 flex flex-col gap-1.5 text-neutral-500 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4">
              {contactName ? (
                <div className="flex min-w-0 items-center gap-1.5">
                  <PiUser className="h-4 w-4 shrink-0" />
                  <Text size="tiny" className="truncate">
                    {contactName}
                  </Text>
                </div>
              ) : null}
              <div className="flex items-center gap-1.5">
                <PiClock className="h-4 w-4 shrink-0" />
                <Text size="tiny">{formatDate(execution.createdAt)}</Text>
                {duration ? <Text size="tiny">· {duration}</Text> : null}
              </div>
            </div>

            {outcome === ExecutionOutcome.BLOCKED ? (
              <div className="mt-3 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2">
                <Text
                  size="tiny"
                  weight="semibold"
                  className="text-warning-800"
                >
                  {blockerCategory}
                </Text>
                {blockerContext ? (
                  <Text size="tiny" className="mt-0.5 text-warning-800">
                    {blockerContext}
                  </Text>
                ) : null}
                <Text size="small" className="mt-0.5 text-warning-900">
                  {blockerReason}
                </Text>
              </div>
            ) : null}
          </div>

          <div className="flex min-w-0 items-center justify-between gap-2 border-t border-neutral-100 pt-3 lg:max-w-72 lg:justify-end lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0">
            <Text
              size="tiny"
              className="min-w-0 truncate font-mono text-neutral-400"
            >
              {execution.executionId}
            </Text>
            <Tooltip
              size="xs"
              placement="top"
              content={
                isCopied
                  ? t('automations.aiAgents.actions.copied')
                  : t('automations.aiAgents.actions.copyId')
              }
            >
              <Button
                type="button"
                variant="neutral"
                appearance="ghost"
                size="xs"
                isIconOnly
                aria-label={
                  isCopied
                    ? t('automations.aiAgents.actions.copied')
                    : t('automations.aiAgents.actions.copyId')
                }
                onClick={(event) => {
                  event.stopPropagation();
                  void copyToClipboard(execution.executionId);
                }}
              >
                {isCopied ? (
                  <PiCheck className="h-4 w-4 text-success-600" />
                ) : (
                  <PiCopy className="h-4 w-4" />
                )}
              </Button>
            </Tooltip>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default EmployeeExecutionCard;
